import type { PlantRoleId, RoleTable } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { Case } from '@/versions/v1/schema/case';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import {
  INITIAL_OPERATOR_ROUNDS,
  roundSubmittedAt,
  type OperatorRoundState,
} from '@/versions/v1/shared/OperatorRoundEngine';
import { applyUtilization, plantStrip } from '@/versions/v1/modules/ingestion/Scene.transform';
import {
  NODE_INSIGHTS,
  OPERATOR_TASK_PLAN,
  RECIRCULATING_LOOPS,
  ROLE_ALERTS,
  SAMPLE_POINTS,
  STREAMS,
  SUBPROCESSES,
  UNIT_OPERATIONS,
  UTILIZATION_RANKS,
  ZN_PH_SETPOINT,
} from '@/versions/v1/shared/bunker-hill-seed';
import { CASES, RECOMMENDED_ACTIONS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { CONTROL_PLAN_ZN_ROWS } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { readingsAt, seriesAt, valueAt, SERIES_BY_READING_ID } from '@/versions/v1/shared/series.derive';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { HOURLY_FEED_READING_IDS } from '@/versions/v1/shared/bunker-hill-readings';
import { addMinutes, diffMinutes, formatCaseClock, isCaseThisShift, previousShiftWindow, shiftOf } from '@/versions/v1/shared/clock.derive';
import { formatPtDelta, recoveryPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { assigneeLabel, casesWithSession, respondByLabel } from '@/versions/v1/modules/advisory/Scene.transform';
import { OPEN_CASE_STATES } from '@/versions/v1/modules/advisory/RoleScreen.transform';
import {
  controlLoops,
  operatorReadingsTable,
  parseDwellHours,
  readingNameFor,
  readingRows,
} from '@/versions/v1/modules/process/Scene.transform';
import { roleTable } from './RoleLanding.transform';
import { lastShiftItem, missedSamples, opsShiftClock, znPhSetpointAt } from './OpsSupervisorLanding.transform';
import type { LandingStripCell } from './GmLeadBand.transform';

const OPS_TASK_STATIC_REWRITES: [RegExp, string][] = [
  // Zn pH 10.8 is a DCS control-loop setpoint (cro-znph: "SP 10.8–11.2"),
  // not a ramp/design production basis — "setpoint" names it, "ramp"
  // would not (ruling #258 round 2, "captions vs tone").
  [/Zn pH target/i, 'Zn pH setpoint'],
];

/**
 * Ops-supervisor shift task copy — bare "target" leaves the glass.
 *
 * The "backfill target confirm" rewrite used to hardcode "backfill ramp
 * confirm" because rd-unit-ps01-paste's targetBasis was 'ramp'. `pl.D1`
 * round 2 (#142) relabelled that reading's targetBasis to 'budget' (same
 * 350 t/d figure, adopted as the Budget track), so the word here must
 * track `readingComparisonBasis` too rather than stay a second hardcode.
 */
export function opsSupervisorTaskText(task: string, state: SessionState): string {
  const rewritten = OPS_TASK_STATIC_REWRITES.reduce(
    (text, [pattern, replacement]) => text.replace(pattern, replacement),
    task,
  );
  if (/backfill target confirm/i.test(rewritten)) {
    const pasteReading = readingsAt(state).find((r) => r.id === 'rd-unit-ps01-paste');
    const basisWord = pasteReading
      ? (readingComparisonBasis(pasteReading)?.label ?? 'Budget').toLowerCase()
      : 'budget';
    return rewritten.replace(/backfill target confirm/i, `backfill ${basisWord} confirm`);
  }
  return rewritten;
}

// ============================================================================
// polish.P16 (#703): the Operator landing, rebuilt to the locked comp
// `.impeccable/mocks/decision/landing-operator.html` A, finished with B's
// round sheet grouped by area and C's "Closed this shift" list. Four view
// cells (Area status · Round sheet · Guidance · Safety) beside the Process
// card, then one panel row per view. Every figure reads a seed, a series, an
// engine or another route's transform at the cursor; nothing is typed, no
// money reaches this persona (adjudication 1), and nothing on a render path
// throws: a missing reading or row degrades to the closed honest state.
// ============================================================================

export type OperatorTabId = 'area' | 'round' | 'guidance' | 'safety';

/** One run of a line: the lead term is `strong`; `tone` colours only an abnormal reading. */
export type OperatorRun = { text: string; strong?: boolean; tone?: 'ok' | 'warn' | 'bad' };

/** The closed honest state for a figure the seed does not carry (DESIGN.md § Decided). */
const OP_UNMEASURED = 'unmeasured';

const hmOf = (stamp: string): string => stamp.split('T')[1] ?? stamp;
const minutesOfHm = (hhmm: string): number => Number(hhmm.slice(0, 2)) * 60 + Number(hhmm.slice(3, 5));
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A reading at the cursor, or the seed row, or nothing — never a throw. */
function readingAt(state: SessionState, id: string) {
  return readingsAt(state).find((row) => row.id === id) ?? readingByIdLookup(id);
}

/** The page subtitle: the running shift's window and what the landing reads against. */
export function operatorSubtitle(state: SessionState): string {
  const clock = opsShiftClock(state);
  const kind = clock.kind === 'day' ? 'Day' : 'Night';
  return `${kind} shift ${clock.start}–${clock.end} · readings against their band, setpoint or design`;
}

// ---- The circuit read (Area status) ----------------------------------------------

export type OperatorAreaRow = {
  id: string;
  name: string;
  figure: string;
  unit: string;
  /** The comparison the reading is judged against; null where none is seeded (adjudication 12). */
  against: OperatorRun[] | null;
  also: OperatorRun[];
  /** A plant fact chip after the also line (`bottleneck`). */
  chip?: string;
  /** Warn only when the reading sits outside its band (adjudication 6). */
  offBand: boolean;
  /** Which side of its band an off-band reading sits. */
  side?: 'below' | 'above';
  /** The reading judged against a band, by the operator's name for it (`Zn cell pH 10.2 · 10.8–11.2`). */
  banded?: { parameter: string; reading: string; range: string };
};

/** The operator's name for a reading (`Zn cell pH`), from the Record & submit readings. */
function operatorName(readingId: string): string | undefined {
  return readingNameFor(readingId, operatorReadingsTable());
}

const fmtT = (n: number) => Math.round(n).toLocaleString('en-US');
/** A no-break space: a figure and its unit never wrap apart (`88.2 %`, not `88.2 / %`). */
const NBSP = '\u00a0';

function recoveryRuns(state: SessionState, id: string, qualifier: string): OperatorRun[] {
  const rec = readingAt(state, id);
  if (rec?.value == null || rec.design == null) return [{ text: `rec ${OP_UNMEASURED}` }];
  const delta = recoveryPtDelta(rec.value, rec.design);
  const runs: OperatorRun[] = [
    { text: 'rec ' },
    { text: `${rec.value.toFixed(1)}${NBSP}%`, strong: true },
    { text: qualifier ? ` ${qualifier} · ` : ' · ' },
    // Recovery leads against design, the Budget secondary (adjudication 5).
    { text: `${formatPtDelta(delta).replace(' ', NBSP)} vs ${rec.design.toFixed(1)}${NBSP}% design`, tone: delta < 0 ? 'warn' : undefined },
  ];
  if (rec.target != null) runs.push({ text: ` · Budget ${rec.target.toFixed(1)}${NBSP}%` });
  return runs;
}

/** `24-h avg` for a recovery whose series is a daily point. */
function recoveryQualifier(readingId: string): string {
  const series = SERIES_BY_READING_ID[readingId];
  return series?.cadence === 'daily' ? '24-h avg' : '';
}

function parseNumber(text: string): number | null {
  const match = text.replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

/** The Zn flotation pH against the one setpoint field and the Control Plan band (P13). */
export function operatorZnPh(state: SessionState): {
  value: number;
  sp: { value: number; since?: string; before: number };
  band: { lo: number; hi: number };
  tag: string;
} | null {
  const reading = readingAt(state, ZN_PH_SETPOINT.readingId);
  const value = valueAt(ZN_PH_SETPOINT.readingId, state) ?? reading?.value;
  const band = CONTROL_PLAN_ZN_ROWS.find((row) => row.id === ZN_PH_SETPOINT.controlPlanRowId)?.specOverlay;
  if (value === undefined || !band || band.lo === undefined || band.hi === undefined) return null;
  const sp = znPhSetpointAt(state);
  return { value, sp: { ...sp, before: ZN_PH_SETPOINT.before }, band: { lo: band.lo, hi: band.hi }, tag: reading?.tag ?? '' };
}

/** Water: the thickener overflows recycled to the process water system, and the fresh make-up (STREAMS). */
function waterFlows(): { recycle: string | null; makeup: string | null } {
  // Only a stream with a measured flow counts (the concentrate overflow carries none).
  const into = STREAMS.filter((row) => row.to === 'sp-water' && /\d/.test(row.nominalFlow));
  return {
    recycle: into.find((row) => row.isRecycle)?.nominalFlow ?? null,
    makeup: into.find((row) => !row.isRecycle)?.nominalFlow ?? null,
  };
}

/**
 * The seven areas in plant order, each with one reading against its band or
 * comparison (the old KPI tiles fold into these rows, said once: adjudication
 * 4). Grind carries the last completed shift through P13's derivation
 * (adjudication 2); the Zn float row reads AT-150 against the current SP from
 * `ZN_PH_SETPOINT` (adjudication 13).
 */
export function operatorAreaRows(state: SessionState): OperatorAreaRow[] {
  const ranks = applyUtilization(UTILIZATION_RANKS, 'confirmed', state);
  const readings = readingsAt(state);
  const nodes = plantStrip(ranks, STREAMS, RECIRCULATING_LOOPS, SUBPROCESSES, UNIT_OPERATIONS, readings);
  const ore = readingAt(state, 'rd-kpi-ore-td');
  const feed = ore?.value != null ? ore.value / 24 : null;
  const feedDesign = ore?.design != null ? ore.design / 24 : null;
  return nodes.map((node): OperatorAreaRow => {
    const base = { id: node.id, name: node.shortName, offBand: false };
    switch (node.id) {
      case 'sp-crush':
        return {
          ...base,
          figure: feed != null ? feed.toFixed(1) : OP_UNMEASURED,
          unit: 'stph feed',
          against: feedDesign != null ? [{ text: `vs ${feedDesign.toFixed(1)}`, strong: true }, { text: ' design' }] : null,
          also: node.sub ? [{ text: node.sub.split('\n').at(-1) ?? '' }] : [],
        };
      case 'sp-grind': {
        const p80 = readingAt(state, 'rd-spec-p80');
        const shift = lastShiftItem(state);
        const window = previousShiftWindow(state.cursor);
        const also: OperatorRun[] =
          shift.value === 'unmeasured'
            ? [{ text: `${window.kind} shift ${OP_UNMEASURED}` }]
            : [
                { text: `${window.kind} shift ` },
                { text: `${shift.value} t`, strong: true },
                { text: ` ${shift.captionLead ?? ''}`, tone: shift.tone === 'ok' ? 'ok' : shift.tone === 'warn' ? 'warn' : undefined },
                { text: shift.caption ?? '' },
              ];
        return {
          ...base,
          figure: p80?.value != null ? String(p80.value) : OP_UNMEASURED,
          unit: 'µm P80',
          against: p80?.design != null ? [{ text: `vs ${p80.design}`, strong: true }, { text: ' design' }] : null,
          also,
          chip: node.isBottleneck ? 'bottleneck' : undefined,
        };
      }
      case 'sp-pb': {
        const ph = readingAt(state, 'rd-kpv-pb-ph');
        const ag = readingAt(state, 'rd-spec-ag-grade');
        const band = ph?.band;
        const off = ph?.value != null && band?.lo != null && band.hi != null && (ph.value < band.lo || ph.value > band.hi);
        const also = recoveryRuns(state, 'rd-kpi-pb-rec', recoveryQualifier('rd-kpi-pb-rec'));
        if (ag?.value != null) {
          also.push({ text: ' · Ag ' }, { text: ag.value.toFixed(2), strong: true }, { text: ` oz/t${ag.design != null ? ` vs ${ag.design.toFixed(2)} design` : ''}` });
        }
        const pbName = operatorName('rd-kpv-pb-ph');
        return {
          ...base,
          figure: ph?.value != null ? ph.value.toFixed(1) : OP_UNMEASURED,
          unit: 'pH',
          against: band?.lo != null && band.hi != null ? [{ text: `${band.lo.toFixed(1)}–${band.hi.toFixed(1)}`, strong: true }, { text: ' band' }] : null,
          also,
          offBand: !!off,
          side: off && ph?.value != null && band?.lo != null ? (ph.value < band.lo ? 'below' : 'above') : undefined,
          banded:
            pbName && ph?.value != null && band?.lo != null && band.hi != null
              ? { parameter: pbName, reading: ph.value.toFixed(1), range: `${band.lo.toFixed(1)}–${band.hi.toFixed(1)}` }
              : undefined,
        };
      }
      case 'sp-zn': {
        const zn = operatorZnPh(state);
        const also = recoveryRuns(state, 'rd-kpi-zn-rec', recoveryQualifier('rd-kpi-zn-rec'));
        if (!zn) return { ...base, figure: OP_UNMEASURED, unit: 'pH', against: null, also };
        const off = zn.value < zn.band.lo || zn.value > zn.band.hi;
        const side = zn.value < zn.band.lo ? 'below' : zn.value > zn.band.hi ? 'above' : undefined;
        const delta = zn.value - zn.sp.value;
        const znName = operatorName(ZN_PH_SETPOINT.readingId);
        return {
          ...base,
          figure: zn.value.toFixed(1),
          unit: 'pH',
          against: [
            { text: `${delta >= 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)}`, strong: true, tone: off ? 'warn' : undefined },
            { text: ` vs SP ${zn.sp.value.toFixed(1)} · band ${zn.band.lo.toFixed(1)}–${zn.band.hi.toFixed(1)}` },
          ],
          also,
          offBand: off,
          side,
          banded: znName
            ? { parameter: znName, reading: zn.value.toFixed(1), range: `${zn.band.lo.toFixed(1)}–${zn.band.hi.toFixed(1)}` }
            : undefined,
        };
      }
      case 'sp-filter': {
        const uf = readingAt(state, 'rd-unit-tkzn-uf');
        return {
          ...base,
          figure: uf?.value != null ? `${uf.value} %` : OP_UNMEASURED,
          unit: 'UF',
          against: null,
          // The Zn thickener's key-parameter copy carries its overflow read.
          also: node.sub ? [{ text: node.sub }] : [],
        };
      }
      case 'sp-paste': {
        const paste = readingAt(state, 'rd-unit-ps01-paste');
        const basis = paste ? readingComparisonBasis(paste) : null;
        const flow = operatorReadings(state).find((row) => /paste backfill/i.test(row.parameter));
        const flowValue = flow ? parseNumber(flow.reading) : null;
        const [lo, hi] = flow ? flow.range.split('–').map((part) => parseNumber(part)) : [null, null];
        const off = flowValue != null && lo != null && hi != null && (flowValue < lo || flowValue > hi);
        return {
          ...base,
          figure: paste?.value != null ? fmtT(paste.value) : OP_UNMEASURED,
          unit: 't/d',
          against: basis ? [{ text: `vs ${fmtT(basis.value)}`, strong: true }, { text: ` ${basis.label}` }] : null,
          also: flow ? [{ text: `backfill flow ${flow.reading} · ${flow.range.replace(/\s*t\/h$/, '')} band` }] : [],
          offBand: off,
          side: off && flowValue != null && lo != null ? (flowValue < lo ? 'below' : 'above') : undefined,
          banded: flow ? { parameter: flow.parameter, reading: flow.reading, range: flow.range } : undefined,
        };
      }
      case 'sp-water': {
        const flows = waterFlows();
        return {
          ...base,
          figure: flows.recycle ? flows.recycle.replace(/\s*gpm$/, '') : OP_UNMEASURED,
          unit: 'gpm',
          against: null,
          also: flows.makeup ? [{ text: `${flows.makeup} makeup` }] : [],
        };
      }
      default:
        return { ...base, figure: node.headline ?? OP_UNMEASURED, unit: '', against: null, also: node.sub ? [{ text: node.sub }] : [] };
    }
  });
}

// ---- The Zn pH trace under the circuit read --------------------------------------

export type OperatorPhTrace = {
  tag: string;
  window: string;
  categories: string[];
  points: number[];
  sp: number;
  band: { lo: number; hi: number };
  domain: [number, number];
  /** The start of the run of readings outside the band that reaches the cursor. */
  outSince: string | null;
  /** SP 11.0 raised from 10.8 at 08:00 by the ZN-REC lime step. */
  raised: { from: number; at: string; by: string } | null;
  good: { text: string; tags: readonly string[] } | null;
  provenance: string;
  synthetic: boolean;
};

/** AT-150 across the running shift against the current setpoint (adjudication 13). */
export function operatorPhTrace(state: SessionState): OperatorPhTrace | null {
  const zn = operatorZnPh(state);
  const series = SERIES_BY_READING_ID[ZN_PH_SETPOINT.readingId];
  if (!zn || !series) return null;
  const shiftStart = shiftOf(state.cursor).start;
  const points = seriesAt(series.id, state).points.filter(
    (point): point is { at: string; value: number } =>
      point.value !== null && diffMinutes(point.at, shiftStart) >= 0 && diffMinutes(point.at, state.cursor) <= 0,
  );
  if (points.length < 2) return null;
  const out = (value: number) => value < zn.band.lo || value > zn.band.hi;
  let since: string | null = null;
  for (let i = points.length - 1; i >= 0 && out(points[i]!.value); i -= 1) since = hmOf(points[i]!.at);
  const values = points.map((point) => point.value);
  const lo = Math.min(...values, zn.band.lo, zn.sp.value);
  const hi = Math.max(...values, zn.band.hi, zn.sp.value);
  const pad = (hi - lo) * 0.12 || 0.1;
  const step = RECOMMENDED_ACTIONS.find((row) => row.id === ZN_PH_SETPOINT.stepActionId);
  const stepCase = step ? casesByIdCode(step.caseId) : undefined;
  const good = NODE_INSIGHTS.find((row) => row.subprocessId === 'sp-zn' && row.kind === 'good');
  return {
    tag: zn.tag,
    window: `${hmOf(shiftStart)}–${hmOf(state.cursor)}`,
    categories: points.map((point) => hmOf(point.at)),
    points: values,
    sp: zn.sp.value,
    band: zn.band,
    domain: [lo - pad, hi + pad],
    outSince: since,
    raised: zn.sp.since ? { from: zn.sp.before, at: zn.sp.since, by: stepCase ?? '' } : null,
    good: good ? { text: good.text.replace(/\.$/, ''), tags: good.tags } : null,
    provenance: series.provenance ?? '',
    synthetic: /\[syn\]/.test(series.provenance ?? ''),
  };
}

function casesByIdCode(caseId: string): string | undefined {
  return CASES.find((row) => row.id === caseId)?.code;
}

// ---- Tasks: the round sheet and Next for me ----------------------------------------

export type OperatorTaskState = 'done' | 'in-progress' | 'overdue' | 'due';

export type OperatorTask = {
  id: string;
  text: string;
  category: string;
  area: string;
  due: string | null;
  state: OperatorTaskState;
  /** The right-hand when: the due time, `in progress`, or `overdue · 10:00`. */
  when: string;
  /** The Advisory ask the task answers (the SMBS check for ZN-REC). */
  ask?: { caseId: string; code: string; text: string };
  /** The immediate response the task carries out, and the safety alert it works under. */
  response?: { caseId: string; code: string; at: string };
  ppe?: string;
  /** The missed sample a sampling task leaves behind (`SA08 Zn rougher conc · sample missed at 10:00`). */
  missed?: string;
  /** The Record & submit round: the manual-readings task, and how the last round read. */
  record?: { readings: number; offBand: number; lastRound: string };
};

const TASK_STATUS: Record<string, OperatorTaskState> = { Done: 'done', 'In progress': 'in-progress' };

function operatorTaskTable(): RoleTable | undefined {
  return roleTable('operator', 'task list');
}

/** The shift the task list belongs to, read from the list's own caption (`… · day 06:00–18:00`). */
export function operatorTaskListShift(): { kind: 'day' | 'night'; start: string; end: string } | null {
  const match = operatorTaskTable()?.caption.match(/\b(day|night) (\d{2}:\d{2})–(\d{2}:\d{2})/i);
  if (!match) return null;
  return { kind: match[1]!.toLowerCase() as 'day' | 'night', start: match[2]!, end: match[3]! };
}

/** The Round sheet's title names the shift its list belongs to, whatever shift the cursor is on. */
export function operatorRoundCaption(): string {
  const shift = operatorTaskListShift();
  return shift ? `Round sheet · ${shift.kind} ${shift.start}–${shift.end}` : 'Round sheet';
}

/** Minutes into the list's shift while the cursor is on a shift of that kind; null otherwise, when the list is not the running round. */
function listShiftMinutes(state: SessionState): number | null {
  const shift = operatorTaskListShift();
  return shift && shiftOf(state.cursor).kind === shift.kind ? minutesOfHm(hmOf(state.cursor)) : null;
}

export function operatorReadings(state: SessionState) {
  const table = operatorReadingsTable();
  return table ? readingRows(table, state) : [];
}

/** A reading row outside its range reads `Below band` or `Above band` (adjudication 11); a range that does not parse reads no word. */
export function readingBandWord(reading: string, range: string): 'Below band' | 'Above band' | null {
  const value = parseNumber(reading);
  const [lo, hi] = range.split('–').map((part) => parseNumber(part));
  if (value == null || lo == null || hi == null) return null;
  if (value < lo) return 'Below band';
  if (value > hi) return 'Above band';
  return null;
}

/**
 * The last round at or before the cursor. The rounds are the due times on
 * the Operator's task plan (`OPERATOR_TASK_PLAN`: 06:00, 07:00, 10:00, 12:00
 * …), each a walk of the plant; before the day's first, the last round is the
 * previous day's final one.
 */
export function operatorLastRound(state: SessionState): SeriesTime | null {
  const times = [...new Set(OPERATOR_TASK_PLAN.map((entry) => entry.due))].sort();
  if (times.length === 0) return null;
  const date = state.cursor.slice(0, 10);
  const today = times.filter((time) => `${date}T${time}` <= state.cursor);
  if (today.length > 0) return `${date}T${today[today.length - 1]}`;
  return `${addMinutes(`${date}T00:00`, -1).slice(0, 10)}T${times[times.length - 1]}`;
}

/** A figure written at the decimals and grouping of the seed string it replaces (`72.1%`, `1,260 g/L`). */
function withFigure(seedText: string, value: number): string {
  const match = seedText.match(/-?\d[\d,]*(?:\.(\d+))?/);
  if (!match) return seedText;
  const decimals = match[1]?.length ?? 0;
  const figure = match[0].includes(',')
    ? value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : value.toFixed(decimals);
  return seedText.replace(match[0], figure);
}

export type OperatorRecordRow = {
  id: string;
  parameter: string;
  /** The reading at the last round: its series at that time, or the seed where it has no series. */
  last: string;
  range: string;
  band: 'Below band' | 'Above band' | null;
  /** Off its range: the band word, or the seed's flag where the range does not parse. */
  off: boolean;
};

/** The Record & submit readings with "Last" at the last round before the cursor (the round the list names is the one entered). */
export function operatorRecordRows(state: SessionState): OperatorRecordRow[] {
  const round = operatorLastRound(state);
  const at: SessionState | null = round ? { ...state, cursor: round } : null;
  return operatorReadings(state).map((row) => {
    const hasSeries = row.readingId !== undefined && (SERIES_BY_READING_ID[row.readingId] !== undefined || HOURLY_FEED_READING_IDS.includes(row.readingId));
    const value = at && hasSeries ? valueAt(row.readingId!, at) : undefined;
    const last = value !== undefined ? withFigure(row.reading, value) : row.reading;
    const band = readingBandWord(last, row.range);
    const [lo, hi] = row.range.split('–').map((part) => parseNumber(part));
    const parses = lo != null && hi != null;
    return { id: row.id, parameter: row.parameter, last, range: row.range, band, off: parses ? band !== null : row.statusTone === 'warn' };
  });
}

/** `1 below band last round`, `all in band last round`: the Record & submit readings at the last round. */
function lastRoundRead(rows: readonly OperatorRecordRow[]): string {
  const below = rows.filter((row) => row.band === 'Below band').length;
  const above = rows.filter((row) => row.band === 'Above band').length;
  const off = rows.filter((row) => row.off).length;
  if (off === 0) return 'all in band last round';
  if (below + above === off && below > 0 && above > 0) return `${below} below · ${above} above band last round`;
  if (below === off) return `${below} below band last round`;
  if (above === off) return `${above} above band last round`;
  return `${off} off range last round`;
}

/** The SAMPLE_POINTS code of a sample grid stream (`Zn rougher conc (4-hrly)` → SA08). */
function samplePointCode(stream: string): string | undefined {
  const name = stream.replace(/\s*\(.*\)$/, '').toLowerCase();
  return SAMPLE_POINTS.find((point) => point.stream.toLowerCase().startsWith(name))?.id;
}

/**
 * The thirteen shift tasks with their area and due time (`OPERATOR_TASK_PLAN`),
 * marked overdue when the due time is before the cursor on the shift the list
 * belongs to (adjudication 7); the ask, response and alert links resolve
 * against Advisory and the safety alerts. A round submitted this session
 * (`OperatorRoundEngine`) reads done at the time it was submitted.
 */
export function operatorTasks(
  state: SessionState,
  advisory: AdvisorySessionState,
  rounds: OperatorRoundState = INITIAL_OPERATOR_ROUNDS,
): OperatorTask[] {
  const table = operatorTaskTable();
  if (!table) return [];
  const now = listShiftMinutes(state);
  const cases = casesWithSession(state, advisory);
  const misses = missedSamples();
  const readings = operatorRecordRows(state);
  return table.rows.map((row) => {
    const [category = '', text = '', status = ''] = row.cells;
    const plan = OPERATOR_TASK_PLAN.find((entry) => entry.taskId === row.id);
    const due = plan?.due ?? null;
    const submitted = category === 'RECORD' ? roundSubmittedAt(rounds, row.id, state.cursor) : null;
    const seeded = submitted ? 'done' : TASK_STATUS[status] ?? 'due';
    const late = seeded !== 'done' && due !== null && now !== null && minutesOfHm(due) < now;
    const state_: OperatorTaskState = late ? 'overdue' : seeded;
    const when = submitted
      ? hmOf(submitted)
      : state_ === 'in-progress'
        ? 'in progress'
        : state_ === 'overdue'
          ? `overdue · ${due}`
          : due ?? '';
    const task: OperatorTask = { id: row.id, text, category, area: plan?.area ?? 'shift', due, state: state_, when };
    if (plan?.askId) {
      const owner = cases.find((row_) => row_.evidenceRequests.some((ev) => ev.id === plan.askId));
      const ask = owner?.evidenceRequests.find((ev) => ev.id === plan.askId);
      if (owner && ask && ask.status === 'open') task.ask = { caseId: owner.id, code: owner.code ?? '', text: ask.text };
    }
    if (plan?.caseId) {
      const response = cases.find((row_) => row_.id === plan.caseId);
      if (response) task.response = { caseId: response.id, code: response.code ?? '', at: formatCaseClock(response.raisedAt, state.cursor) };
    }
    if (plan?.alertId) {
      const alert = ROLE_ALERTS.find((row_) => row_.id === plan.alertId);
      // `Zn pH low — full PPE near Zn cell 4` → the protection it names.
      task.ppe = alert?.text.split(' — ')[1]?.split(' near ')[0];
    }
    if (category === 'SAMPLE' && due && state_ !== 'done') {
      const miss = misses.find((row_) => row_.slot === due);
      if (miss) {
        const code = samplePointCode(miss.stream);
        task.missed = `${code ? `${code} ` : ''}${miss.stream.replace(/\s*\(.*\)$/, '')} · sample missed at ${miss.slot}`;
      }
    }
    if (category === 'RECORD') {
      task.record = { readings: readings.length, offBand: readings.filter((row_) => row_.off).length, lastRound: lastRoundRead(readings) };
    }
    return task;
  });
}

export type OperatorRoundSummary = { done: number; total: number; overdue: number; inProgress: number; next: string | null };

/**
 * The Round sheet cell. `next` is the earliest task still due that is ahead
 * of the cursor on the list's own clock (06:00–18:00); once the list's shift
 * has ended (a night cursor after 18:00) no task is ahead, so there is none.
 */
export function operatorRoundSummary(tasks: readonly OperatorTask[], state: SessionState): OperatorRoundSummary {
  const now = minutesOfHm(hmOf(state.cursor));
  const upcoming = tasks
    .filter((task) => task.state === 'due' && task.due !== null && minutesOfHm(task.due) > now)
    .map((task) => task.due!)
    .sort();
  return {
    done: tasks.filter((task) => task.state === 'done').length,
    total: tasks.length,
    overdue: tasks.filter((task) => task.state === 'overdue').length,
    inProgress: tasks.filter((task) => task.state === 'in-progress').length,
    next: upcoming[0] ?? null,
  };
}

const NEXT_RANK: Record<OperatorTaskState, number> = { 'in-progress': 0, overdue: 1, due: 2, done: 3 };

/** Next for me: the open tasks in the order to do them — in progress, overdue,
 *  then by due time — the first `limit` shown and the rest counted. */
export function operatorNextForMe(tasks: readonly OperatorTask[], limit = 6): { rows: OperatorTask[]; more: number } {
  const open = tasks
    .map((task, index) => ({ task, index }))
    .filter(({ task }) => task.state !== 'done')
    .sort(
      (a, b) =>
        NEXT_RANK[a.task.state] - NEXT_RANK[b.task.state] ||
        (a.task.due ?? '99:99').localeCompare(b.task.due ?? '99:99') ||
        a.index - b.index,
    )
    .map(({ task }) => task);
  return { rows: open.slice(0, limit), more: Math.max(0, open.length - limit) };
}

export type OperatorRoundGroup = {
  id: string;
  name: string;
  /** The area's reading in the group header, or the group's task count. */
  header: OperatorRun[];
  offBand: boolean;
  tasks: OperatorTask[];
  /** Good and bad looks like (or the act a response names) under the group's first task. */
  guide: OperatorRun[] | null;
};

const NON_AREA_GROUPS: readonly { id: string; name: string }[] = [
  { id: 'samples', name: 'Samples' },
  { id: 'shift', name: 'Shift' },
];

/** B's round sheet: every task under the area it belongs to, plant order, each
 *  header carrying that area's reading; the sample and shift tasks close it. */
export function operatorRoundSheet(state: SessionState, tasks: readonly OperatorTask[]): OperatorRoundGroup[] {
  const areas = operatorAreaRows(state);
  const zeroTouch = operatorTaskResponses(tasks);
  const groups: OperatorRoundGroup[] = areas.map((area) => {
    const own = tasks.filter((task) => task.area === area.id);
    const good = NODE_INSIGHTS.find((row) => row.subprocessId === area.id && row.kind === 'good');
    const bad = NODE_INSIGHTS.find((row) => row.subprocessId === area.id && row.kind === 'bad');
    const act = zeroTouch.find((row) => row.area === area.id);
    const clip = (text: string) => text.replace(/\.$/, '').split(' — ')[0]!;
    const guide: OperatorRun[] | null =
      own.length > 0 && good
        ? [
            { text: 'Good', strong: true },
            { text: ` ${clip(good.text)}` },
            ...(area.offBand && act
              ? [{ text: ' · ' }, { text: 'Act', strong: true }, { text: ` ${act.text}` }]
              : bad
                ? [{ text: ' · ' }, { text: 'Bad', strong: true }, { text: ` ${clip(bad.text)}` }]
                : []),
          ]
        : null;
    return {
      id: area.id,
      name: area.name,
      header: [{ text: `${area.figure} ${area.unit}`.trim(), strong: true, tone: area.offBand ? 'warn' : undefined }, ...(area.against ? [{ text: ' ' }, ...area.against.map((run) => ({ text: run.text }))] : [])],
      offBand: area.offBand,
      tasks: own,
      guide,
    };
  });
  for (const group of NON_AREA_GROUPS) {
    const own = tasks.filter((task) => task.area === group.id);
    groups.push({ id: group.id, name: group.name, header: [{ text: plural(own.length, 'task') }], offBand: false, tasks: own, guide: null });
  }
  return groups;
}

/** The immediate response a task carries, as the Act line of its area (`raise lime SP (ZN-PH-01)`). */
function operatorTaskResponses(tasks: readonly OperatorTask[]): { area: string; text: string }[] {
  return tasks.flatMap((task) => {
    if (!task.response) return [];
    const caseItem = CASES.find((row) => row.id === task.response!.caseId);
    const action = caseItem?.title.split(': ').slice(1).join(': ');
    return action ? [{ area: task.area, text: `${action} (${task.response.code})` }] : [];
  });
}

// ---- Record & submit ---------------------------------------------------------------

export type OperatorRecordSubmit = {
  /** The RECORD task the card enters, and its round time (`12:00`). */
  taskId: string | null;
  round: string | null;
  rows: OperatorRecordRow[];
  /** The values entered this session, by reading row id. */
  entries: Readonly<Record<string, string>>;
  /** `HH:MM` once the round is submitted by the cursor; null while it is open. */
  submitted: string | null;
  /** Every reading has an entry, so the round can be submitted. */
  complete: boolean;
};

export function operatorRecordSubmit(
  state: SessionState,
  tasks: readonly OperatorTask[],
  rounds: OperatorRoundState = INITIAL_OPERATOR_ROUNDS,
): OperatorRecordSubmit {
  const task = tasks.find((row) => row.record) ?? null;
  const rows = operatorRecordRows(state);
  const entries = task ? rounds.get(task.id)?.entries ?? {} : {};
  const submitted = task ? roundSubmittedAt(rounds, task.id, state.cursor) : null;
  return {
    taskId: task?.id ?? null,
    round: task?.due ?? null,
    rows,
    entries,
    submitted: submitted ? hmOf(submitted) : null,
    complete: rows.every((row) => (entries[row.id] ?? '').trim() !== ''),
  };
}

/** The reading the project home's Operator row repeats: the landing's lead flag,
 *  the first area off its band (the Area status cell names it), as the operator
 *  names that reading, with its side and band (polish.P23 → P16). */
export function operatorFlaggedReading(state: SessionState): { parameter: string; reading: string; status: string; range: string } | null {
  const row = operatorAreaRows(state).find((candidate) => candidate.offBand && candidate.side && candidate.banded);
  if (!row?.banded || !row.side) return null;
  return { ...row.banded, status: `${row.side === 'below' ? 'Below' : 'Above'} band` };
}

// ---- Guidance -----------------------------------------------------------------------

const GUIDANCE_SCOPE: readonly PlantRoleId[] = ['cro', 'operator'];

/** The open cases the engine counts as "awaiting my action" for the CRO and
 *  operator (Advisory's role screen, `RoleScreen.transform.ts`): the Control
 *  room landing reads the same figure (comp, other personas). */
export function operatorGuidanceCases(state: SessionState, advisory: AdvisorySessionState): Case[] {
  return casesWithSession(state, advisory).filter(
    (row) => GUIDANCE_SCOPE.includes(row.assignedTo) && OPEN_CASE_STATES.includes(row.state),
  );
}


const ASK_LEAD: Partial<Record<NonNullable<Case['kind']>, string>> = {
  'zero-touch': 'Immediate response',
  'lab-sample': 'Evidence',
  'control-mode': 'Control mode',
  visual: 'Walk out',
};

export type OperatorGuidanceCard = {
  caseId: string;
  code: string;
  title: string;
  meta: string;
  state: string;
  observation: string;
  ask: { lead: string; text: string };
  holder: string;
  escalation: string | null;
  /** Routed to the operator seat itself (`AIR-VALVE routed to me`). */
  toMe: boolean;
};

export type OperatorQuietAsk = { caseId: string; code: string; text: string; meta: string; state: string };
export type OperatorClosed = { caseId: string; code: string; at: string; text: string; closed: string };
export type OperatorAlsoAsked = { caseId: string; code: string; count: number; kind: string; items: string[] };

export type OperatorGuidance = {
  cards: OperatorGuidanceCard[];
  /** Walk-outs asked of the operator on cases another seat holds (TRAMP-CV001). */
  quiet: OperatorQuietAsk[];
  closed: OperatorClosed[];
  /** Lab-sample asks, one count line per case (adjudication 9). */
  alsoAsked: OperatorAlsoAsked[];
};

function holderLabel(caseItem: Case): string {
  const seat = assigneeLabel(caseItem.handler?.role ?? caseItem.assignedTo);
  return caseItem.handler ? `${caseItem.handler.name} · ${seat}` : seat;
}

export function operatorGuidance(
  state: SessionState,
  advisory: AdvisorySessionState,
  tasks: readonly OperatorTask[],
): OperatorGuidance {
  const cases = casesWithSession(state, advisory);
  const mine = operatorGuidanceCases(state, advisory);
  const carried = new Set(tasks.flatMap((task) => (task.ask ? [task.ask.caseId] : [])));
  const cards = mine.map((row): OperatorGuidanceCard => {
    const ev = row.evidenceRequests.find((request) => request.status === 'open' && GUIDANCE_SCOPE.includes(request.target));
    const escalation = respondByLabel(row, state.cursor);
    // A ladder position is the ask itself (`Tier 2 · CRO · arrange … · escalates
    // to Met in 20m`): it leads the ask box, and the escalation is not said twice.
    const ladder = row.ladder && escalation ? escalation.split(' · ') : null;
    return {
      caseId: row.id,
      code: row.code ?? '',
      title: row.title,
      meta: `${row.code ?? ''} · ${row.circuit} · ${formatCaseClock(row.raisedAt, state.cursor)}`,
      state: row.state.replace(/-/g, ' '),
      observation: row.observation,
      ask:
        ladder && ladder.length > 2
          ? { lead: ladder.slice(0, 2).join(' · '), text: ladder.slice(2).join(' · ') }
          : { lead: ASK_LEAD[row.kind ?? 'zero-touch'] ?? 'Next step', text: ev?.text ?? row.causeCandidate },
      holder: holderLabel(row),
      escalation: ladder ? null : escalation,
      toMe: row.assignedTo === 'operator' && row.state === 'routed',
    };
  });
  const others = cases.filter((row) => !mine.includes(row) && OPEN_CASE_STATES.includes(row.state) && !carried.has(row.id));
  const quiet = others.flatMap((row) =>
    row.evidenceRequests
      .filter((ev) => ev.status === 'open' && ev.target === 'operator' && ev.kind === 'visual')
      .map((ev) => ({
        caseId: row.id,
        code: row.code ?? '',
        text: ev.text.split('. ')[0]!.replace(/\.$/, ''),
        // One holder statement: a respond-by that escalates to the seat holding
        // the case already names it (`respond by 11:20 → Maint`), so `with …` is not said twice.
        meta: [
          respondByLabel(row, state.cursor),
          row.respondBy?.escalatesTo === row.assignedTo ? null : `with ${assigneeLabel(row.assignedTo)}`,
        ]
          .filter(Boolean)
          .join(' · '),
        state: row.state.replace(/-/g, ' '),
      })),
  );
  const alsoAsked = others.flatMap((row) => {
    const labs = row.evidenceRequests.filter((ev) => ev.status === 'open' && ev.target === 'operator' && ev.kind === 'lab-sample');
    return labs.length > 0
      ? [{ caseId: row.id, code: row.code ?? '', count: labs.length, kind: labs.length === 1 ? 'lab sample' : 'lab samples', items: labs.map((ev) => ev.text) }]
      : [];
  });
  // Closed this shift: the zero-touch responses raised and resolved on the
  // running shift — the engine's own "closed this shift" set (Advisory's role screen).
  const closed = cases
    .filter((row) => row.branch === 'zero-touch' && row.resolvedAt && isCaseThisShift(row.raisedAt, state.cursor))
    .sort((a, b) => a.raisedAt.localeCompare(b.raisedAt))
    .map((row) => ({
      caseId: row.id,
      code: row.code ?? '',
      at: formatCaseClock(row.raisedAt, state.cursor),
      text: row.title.startsWith(`${row.code} — `) ? row.title.slice(`${row.code} — `.length) : row.title,
      closed: formatCaseClock(row.resolvedAt!, state.cursor),
    }));
  return { cards, quiet, closed, alsoAsked };
}

// ---- Safety -----------------------------------------------------------------------

export type OperatorAlert = { id: string; severity: 'high' | 'med' | 'low'; label: string; text: string; time: string };

const SEVERITY_LABEL: Record<OperatorAlert['severity'], string> = { high: 'High', med: 'Med', low: 'Low' };
const SEVERITY_RANK: Record<OperatorAlert['severity'], number> = { high: 0, med: 1, low: 2 };

export function operatorAlerts(): OperatorAlert[] {
  return ROLE_ALERTS.filter((row) => row.roleId === 'operator')
    .map((row) => ({ id: row.id, severity: row.severity, label: SEVERITY_LABEL[row.severity], text: row.text, time: row.time }))
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

export function operatorContacts(): { title: string; rows: { role: string; contact: string }[] } | null {
  const table = roleTable('operator', 'contacts');
  if (!table) return null;
  return {
    title: table.caption.replace(' — ', ' · '),
    rows: table.rows.map((row) => ({ role: row.cells[0] ?? '', contact: row.cells[1] ?? '' })),
  };
}

// ---- The lead band ------------------------------------------------------------------

export function operatorStripCells(
  state: SessionState,
  advisory: AdvisorySessionState,
  rounds: OperatorRoundState = INITIAL_OPERATOR_ROUNDS,
): LandingStripCell<OperatorTabId>[] {
  const areas = operatorAreaRows(state);
  const off = areas.filter((row) => row.offBand);
  const tasks = operatorTasks(state, advisory, rounds);
  const round = operatorRoundSummary(tasks, state);
  const guidance = operatorGuidance(state, advisory, tasks);
  const alerts = operatorAlerts();
  const top = alerts[0];
  const firstOff = off[0];
  const offWord = firstOff ? (firstOff.id === 'sp-zn' || firstOff.id === 'sp-pb' ? 'pH' : firstOff.unit) : '';
  const roundParts = [
    round.inProgress > 0 ? `${round.inProgress} in progress` : null,
    round.next ? `next ${round.next}` : null,
  ].filter(Boolean) as string[];
  const lead = guidance.cards[0];
  return [
    {
      id: 'area',
      name: 'Area status',
      figure: String(off.length),
      figureContext: `of ${areas.length} areas off band`,
      figureFlag: off.length > 0 ? 'warn' : undefined,
      comparisonLead: firstOff ? firstOff.name : 'every area in band',
      comparisonRest: firstOff ? ` · ${offWord} ${bandSide(firstOff)}${off.length > 1 ? ` · ${off.length - 1} more` : ''}` : '',
      actor: 'System',
      actorLabel: 'Historian',
    },
    {
      id: 'round',
      name: 'Round sheet',
      figure: String(round.done),
      figureContext: `of ${round.total} done`,
      comparisonLead: round.overdue > 0 ? `${round.overdue} overdue` : roundParts.shift() ?? '',
      comparisonRest: roundParts.length > 0 ? ` · ${roundParts.join(' · ')}` : '',
      actor: 'Human',
      actorLabel: 'Operator',
    },
    {
      id: 'guidance',
      name: 'Guidance',
      figure: String(guidance.cards.length),
      figureContext: 'with me',
      comparisonLead: lead ? lead.code : 'none open',
      comparisonRest: lead ? ` ${lead.state}${lead.toMe ? ' to me' : ''}${guidance.cards.length > 1 ? ` · ${guidance.cards.length - 1} more` : ''}` : '',
      actor: 'Engine',
    },
    {
      id: 'safety',
      name: 'Safety',
      figure: String(alerts.length),
      figureContext: alerts.length === 1 ? 'alert' : 'alerts',
      comparisonLead: top ? top.label : '',
      comparisonRest: top ? ` · ${top.text.split(' — ')[1] ?? top.text} · ${top.time}` : '',
      actor: 'System',
    },
  ];
}

function bandSide(row: OperatorAreaRow): string {
  return row.side ? `${row.side} band` : 'off band';
}

/** The onward card: control loops on Process — how many are in manual, the
 *  longest and since when (adjudication 10). */
export function operatorLoopsCard(): { manual: number; total: number; tag: string | null; since: string | null } {
  const loops = controlLoops();
  const manual = loops.filter((loop) => loop.mode === 'manual');
  const longest = [...manual].sort((a, b) => (parseDwellHours(b.duration) ?? 0) - (parseDwellHours(a.duration) ?? 0))[0];
  return { manual: manual.length, total: loops.length, tag: longest?.tag ?? null, since: longest?.modeChangedAt ?? null };
}
